import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";

interface JsonViewerProps {
  data: unknown;
  searchText?: string;
  rootName?: string;
  defaultExpanded?: boolean;
  showFullBranchOnMatch?: boolean;
}

const containsSearch = (data: unknown, search: string): boolean => {
  if (!search || data === null || data === undefined) return false;
  const s = search.toLowerCase();
  if (typeof data === "string") return data.toLowerCase().includes(s);
  if (typeof data === "number" || typeof data === "boolean")
    return String(data).toLowerCase().includes(s);
  if (Array.isArray(data))
    return data.some((item) => containsSearch(item, search));
  if (typeof data === "object") {
    return Object.entries(data).some(
      ([k, v]) => k.toLowerCase().includes(s) || containsSearch(v, search),
    );
  }
  return false;
};

const highlight = (text: string, search: string): React.ReactNode => {
  if (search.length < 3) return text;
  const idx = text.toLowerCase().indexOf(search.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className={styles.highlight}>
        {text.slice(idx, idx + search.length)}
      </span>
      {text.slice(idx + search.length)}
    </>
  );
};

const renderValue = (value: unknown, search: string): React.ReactNode => {
  if (value === null) return <span className={styles.null}>null</span>;
  if (value === undefined)
    return <span className={styles.undefined}>undefined</span>;
  if (typeof value === "string")
    return (
      <span className={styles.string}>
        {'"'}
        {highlight(value, search)}
        {'"'}
      </span>
    );
  if (typeof value === "number")
    return (
      <span className={styles.number}>{highlight(String(value), search)}</span>
    );
  if (typeof value === "boolean")
    return (
      <span className={styles.boolean}>{highlight(String(value), search)}</span>
    );
  return <span>{String(value)}</span>;
};

function JsonNode({
  keyName,
  value,
  searchText,
  level,
  defaultExpanded,
  showFullBranchOnMatch,
}: {
  keyName: string;
  value: unknown;
  searchText: string;
  level: number;
  defaultExpanded: boolean;
  showFullBranchOnMatch: boolean;
}) {
  const isExpandable =
    value !== null &&
    typeof value === "object" &&
    Object.keys(value as object).length > 0;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const keyMatches =
    searchText.length >= 3 &&
    keyName.toLowerCase().includes(searchText.toLowerCase());

  useEffect(() => {
    if (searchText.length < 3) {
      setIsExpanded(defaultExpanded);
      return;
    }
    // Expand ancestors leading to a match so it's visible among its siblings,
    // without auto-diving into a node whose own key is the match.
    const matches = containsSearch(value, searchText);
    setIsExpanded(matches);
  }, [searchText, value, defaultExpanded]);

  // Once a branch is open, show every sibling rather than pruning to matches
  // unless the caller wants pruning and this node wasn't opened by its own match.
  const entries =
    isExpandable && isExpanded
      ? showFullBranchOnMatch || searchText.length < 3 || keyMatches
        ? Object.entries(value as object)
        : Object.entries(value as object).filter(
            ([k, v]) =>
              k.toLowerCase().includes(searchText.toLowerCase()) ||
              containsSearch(v, searchText),
          )
      : [];

  return (
    <div style={{ paddingLeft: level > 0 ? 16 : 0 }}>
      <div
        className={styles.row}
        onClick={isExpandable ? () => setIsExpanded(!isExpanded) : undefined}
      >
        {isExpandable && (
          <span className={styles.toggle}>{isExpanded ? "▼" : "▶"}</span>
        )}
        <span className={styles.key}>
          {keyMatches ? highlight(keyName, searchText) : keyName}
        </span>
        {!isExpandable && (
          <>
            <span className={styles.colon}>: </span>
            {renderValue(value, searchText)}
          </>
        )}
      </div>
      {isExpandable && isExpanded && (
        <div>
          {entries.map(([k, v]) => (
            <JsonNode
              key={k}
              keyName={k}
              value={v}
              searchText={searchText}
              level={level + 1}
              defaultExpanded={false}
              showFullBranchOnMatch={showFullBranchOnMatch}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function JsonViewer({
  data,
  searchText = "",
  rootName,
  defaultExpanded = false,
  showFullBranchOnMatch = true,
}: JsonViewerProps) {
  if (data === null || data === undefined) return null;

  if (rootName) {
    return (
      <div className={styles.container}>
        <JsonNode
          keyName={rootName}
          value={data}
          searchText={searchText}
          level={0}
          defaultExpanded={defaultExpanded}
          showFullBranchOnMatch={showFullBranchOnMatch}
        />
      </div>
    );
  }

  if (typeof data === "object") {
    return (
      <div className={styles.container}>
        {Object.entries(data).map(([key, value]) => (
          <JsonNode
            key={key}
            keyName={key}
            value={value}
            searchText={searchText}
            level={0}
            defaultExpanded={defaultExpanded}
            showFullBranchOnMatch={showFullBranchOnMatch}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.container}>{renderValue(data, searchText)}</div>
  );
}
